import { createContext, ReactNode, useEffect, useState } from "react";
import { PluginspaceResponseType } from "./PluginspaceContext";
import { CategoryType, SimpleCategoryType } from "./CategoryContext";
import api from "../services/api";
import { usePluginspace } from "../hooks/usePluginspace";
import { useParams } from "react-router-dom";
import { ItemFractionalType } from "./OrderContext";
import Swal from "sweetalert2";

type ProductsContextType = {
  getProducts: (
    pluginspaceInfo: PluginspaceResponseType,
    newProducts?: ProductType[],
    search?: string,
    page?: number,
    agroupCategory?: boolean,
    categoryId?: number,
    showOutStock?: boolean,
    subcategory?: SimpleCategoryType,
    qtdProductsPagination?: number,
    orderBy?: string
  ) => Promise<ProductsReturnType>;

  getProductsFood: (outStock: boolean) => Promise<ProductFoodType[]>;
  selectProduct: (id: string) => ProductType | null;
  productSelected: ProductType | null;
  selectProductFood: (id: string) => ProductFoodType | null;
  productFoodSelected: ProductFoodType | null;
  organizeFractions: (fractions: ItemFractionalType[]) => ItemFractionalType[];

  saveFilterProductOptions: (
    filterProductOptions: FilterProductOptions
  ) => void;
  getFilterProductOptions: () => FilterProductOptions;
  getGrades: (
    categoriasId: string[],
    noStock: boolean,
    name?: string
  ) => Promise<{ field: string; values: string[] }[]>;
  getGradesByCatalog: (
    catalog_id: string,
    noStock: boolean
  ) => Promise<{ field: string; values: string[] }[]>;
  setOpenModalFiltroPDV: (value: boolean) => void;
  openModalFiltroPDV: boolean;

  getProdutosFiltered: (
    page: number,
    pageSize: number,
    outStock: boolean,
    orderBy?: string
  ) => Promise<ProductsFilterReturnType>;
  getProdutosFilteredByCatalog: (
    catalog_id: string,
    page: number,
    pageSize: number,
    outStock: boolean,
    orderBy?: string,
    firstTime?: boolean
  ) => Promise<ProductsFilterReturnType>;
  // Novas funções para controle de orderBy por página
  saveOrderByForPage: (
    page: "pdv" | "pdvFiltered" | "pdvFilteredCategory",
    orderBy: string,
    category?: string
  ) => void;
  getOrderByForPage: (
    page: "pdv" | "pdvFiltered" | "pdvFilteredCategory",
    category?: string
  ) => string;
  resetOrderByForPage: (
    page: "pdv" | "pdvFiltered" | "pdvFilteredCategory"
  ) => void;
  setCurrentPage: (
    page: "pdv" | "pdvFiltered" | "pdvFilteredCategory",
    category?: string
  ) => void;

  saveCurrentCategoryFilter: (category: string) => void;
  getCurrentCategoryFilter: () => string;
};

export type ProductsFilterReturnType = {
  data: ProductType[];
  hasNextPage: boolean;
  meta?: {
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    itemCount: number;
    page: string;
    pageCount: number;
    take: string;
  } | null;
};

type ProductsContextProviderProps = {
  children: ReactNode;
};

type ImageType = {
  typeimg: string;
  path: string;
  imgsuffix: string;
};

export type VariationType = {
  id: string;
  status: boolean;
  referenceCode: string;
  sku: string;
  price: number;
  promotionalPrice: number;
  inventory: number;
  minimumStock: number;
  context: string;
  img: string;
  locationId: number;
  weight?: number;
  width?: number;
  height?: number;
  depth?: number;
};

type FractionalType = {
  productFractionalId: string;
  id: number;
  productId: string;
  variations: VariationType[];
  item: {
    id: string;
    name: string;
    code: string;
    description: string;
  }[];
};

type OptionalType = {
  id: number;
  name: string;
  required: number;
  type: string;
  description?: string;
  max?: number;
  items: {
    id: number;
    max: number | null;
    selected?: boolean;
    quantity?: number;
    product: {
      id: string;
      name: string;
      code: string;
      variations: VariationType[];
    };
  }[];
};

export type ProductType = {
  id: string;
  name: string;
  description: string;
  code: string;
  status: boolean;
  classificationId: string;
  classification?: CategoryType;
  variations: VariationType[];
  technicalSpecification?: string;
  img?: string;
  images: ImageType[];
  type?: string;
  fractions?: FractionalType[];
  typeFractional?: "proportional" | "biggest" | null;
  quantityFractional?: number | null;
  order: number;
  sellAsFractional?: boolean;
  hasDiscount?: boolean;
  discountValue?: string;
  mark?: any;
};

export type ProductFoodType = {
  id: number;
  name: string;
  type: string;
  img: string;
  unitPrice: number;
  promotionalPrice: number;
  quantity: number;
  stock: number;
  sku: string;
  code: string;
  locationId: number;
  classification: SimpleCategoryType;
  description?: string;
  optionals?: OptionalType[];
  variations: VariationType[];
  images?: Array<any>;
  quantityFractional?: number;
  showFractionalPrice?: boolean;
  showFractionalDescription?: boolean;
  typeFractional?: "proportional" | "biggest";
  fractions?: FractionalType[];
  initialHour?: string;
  finalHour?: string;
  order: number;
};

type ConfigProductType = {
  isPriceGreaterThan: number;
  isInventoryGreaterThan: number;
  name: string;
  status: boolean;
  variationActive: boolean;
  validClassification: boolean;
  classificationIds?: number[] | null;
  locationId?: number | string | null;
};

type ProductSaveType = {
  products: ProductType[];
  page: number;
  qtdProductsPagination: number;
  configProductSearch: ConfigProductType;
  hasNextPage: boolean;
};

export type ProductsReturnType = {
  products: ProductType[];
  hasNextPage: boolean;
  meta: any | null;
};

export type FilterProductOptions = {
  prices: { min: number; max: number };
  name: string;
  categories: string[];
  [key: string]: any; // Allows dynamic fields
};

type OrderByPageControl = {
  pdv: string;
  pdvFiltered: string;
  pdvFilteredCategory: string;
  currentPage: "pdv" | "pdvFiltered" | "pdvFilteredCategory";
  currentCategory?: string;
};

export const ProductsContext = createContext({} as ProductsContextType);

export function ProductsContextProvider(props: ProductsContextProviderProps) {
  const { getToken, getLocations } = usePluginspace();
  const [productsSaved, setProductsSaved] = useState<ProductSaveType[]>([]);
  const [productsFoodSaved, setProductsFoodSaved] = useState<
    ProductFoodType[] | null
  >(null);
  const [productSelected, setProductSelected] = useState<ProductType | null>(
    null
  );
  const [productFoodSelected, setProductFoodSelected] =
    useState<ProductFoodType | null>(null);

  const [filterProductOptions, setFilterProductOptions] =
    useState<FilterProductOptions>({
      prices: { min: 0, max: 0 },
      name: "",
      categories: [],
    });
  const [openModalFiltroPDV, setOpenModalFiltroPDV] = useState<boolean>(false);

  const [orderBy, setOrderBy] = useState<string>("name-asc");
  const [orderByPageControl, setOrderByPageControl] =
    useState<OrderByPageControl>({
      pdv: "name-asc",
      pdvFiltered: "name-asc",
      pdvFilteredCategory: "name-asc",
      currentPage: "pdv",
      currentCategory: undefined,
    });

  // useEffect para inicializar o controle de orderBy por página
  useEffect(() => {
    const storedControl = getOrderByControlFromStorage();
    setOrderByPageControl(storedControl);
  }, []);

  /*
    async function getProductsFood(outStock: boolean) {
        if (productsFoodSaved) {
            return productsFoodSaved
        } else {
            const locations = await getLocations()

            const location = locations.find(el => el.type === 'internal' && el.status === true)
            let url = outStock ? `api/product-food?locationId=${location?.id ? location.id : ''}` : `api/product-food/stock?locationId=${location?.id ? location.id : ''}`;
            const response = await api.get(url, {
                headers: {
                    Authorization: `Bearer ${await getToken()} `,
                },
            });
            setProductsFoodSaved(response.data.data);
            return response.data.data;
        }
    }
    */

  async function getProductsFood(outStock: boolean) {
    if (productsFoodSaved) {
      return productsFoodSaved;
    } else {
      const locations = await getLocations();

      const table = localStorage.getItem("table_number");
      const card = localStorage.getItem("card_number");
      const counter = localStorage.getItem("counter_number");
      const synkar = localStorage.getItem("landmark_synkar");
      const internal = localStorage.getItem("internal_number");

      let locationCode = "withdraw";
      if (table || card) locationCode = "mesa";
      else if (counter) locationCode = "counter";
      else if (synkar) locationCode = "synkar";
      else if (internal) locationCode = "internal-delivery";

      const location = locations.find(
        (el) =>
          el.type === "internal" &&
          el.status === true &&
          el.code === locationCode
      );

      let url = outStock
        ? `api/product-food/products_v2?locationId=${
            location?.id ? location.id : ""
          }`
        : `api/product-food/stock?locationId=${
            location?.id ? location.id : ""
          }`;
      const response = await api.get(url, {
        headers: {
          Authorization: `Bearer ${await getToken()} `,
        },
      });
      setProductsFoodSaved(response.data.data);
      return response.data.data;
    }
  }

  async function getProducts(
    pluginspaceInfo: PluginspaceResponseType,
    newProducts: ProductType[] = [],
    search: string = "",
    page = 1,
    agroupCategory = false,
    categoryId = 0,
    showOutStock = false,
    subcategory?: SimpleCategoryType,
    qtdProductsPagination = 10,
    orderBy = ""
  ) {
    let objSearch: ConfigProductType;
    const locations = await getLocations();

    const table = localStorage.getItem("table_number");
    const card = localStorage.getItem("card_number");
    const counter = localStorage.getItem("counter_number");
    const synkar = localStorage.getItem("landmark_synkar");
    const internal = localStorage.getItem("internal_number");

    let locationCode = "withdraw";
    if (table || card) locationCode = "mesa";
    else if (counter) locationCode = "counter";
    else if (synkar) locationCode = "synkar";
    else if (internal) locationCode = "internal-delivery";

    const location = locations.find(
      (el) =>
        el.type === "internal" && el.status === true && el.code === locationCode
    );

    if (categoryId) {
      objSearch = {
        isPriceGreaterThan: 0,
        isInventoryGreaterThan: showOutStock ? 0 : 1,
        name: search,
        status: true,
        variationActive: true,
        validClassification: true,
        classificationIds: agroupCategory
          ? [subcategory ? subcategory?.id : categoryId]
          : [categoryId],
        //locationId: location?.id ? location.id : "",
      };
    } else {
      objSearch = {
        isPriceGreaterThan: 0,
        isInventoryGreaterThan: showOutStock ? 0 : 1,
        status: true,
        name: search,
        variationActive: true,
        validClassification: true,
        //locationId: location?.id ? location.id : "",
      };
    }

    let alreadySaved = false;
    let productsReturn: ProductsReturnType = {
      products: [],
      hasNextPage: false,
      meta: null,
    };
    productsSaved.forEach((productSaved) => {
      if (
        productSaved.page === page &&
        productSaved.qtdProductsPagination === qtdProductsPagination &&
        ((!productSaved.configProductSearch.classificationIds &&
          !objSearch.classificationIds) ||
          (productSaved.configProductSearch.classificationIds &&
            objSearch.classificationIds &&
            productSaved.configProductSearch.classificationIds[0] ===
              objSearch.classificationIds[0])) &&
        productSaved.configProductSearch.isInventoryGreaterThan ===
          objSearch.isInventoryGreaterThan &&
        productSaved.configProductSearch.isPriceGreaterThan ===
          objSearch.isPriceGreaterThan &&
        productSaved.configProductSearch.name === objSearch.name &&
        productSaved.configProductSearch.status === objSearch.status &&
        productSaved.configProductSearch.variationActive ===
          objSearch.variationActive &&
        productSaved.configProductSearch.validClassification ===
          objSearch.validClassification
      ) {
        alreadySaved = true;
        productsReturn.products = productSaved.products;
        productsReturn.hasNextPage = productSaved.hasNextPage;
      }
    });
    if (alreadySaved && false) {
      return productsReturn;
    } else {
      if (pluginspaceInfo!.type === "food") {
        return await api
          .post(
            `api/product/filterfood?page=${page}&take=${qtdProductsPagination}`,
            objSearch,
            {
              headers: {
                Authorization: `Bearer ${await getToken()} `,
              },
            }
          )
          .then(async (response) => {
            const hasNextPage = response.data.meta.hasNextPage;
            response.data.data.map((product: ProductType) => {
              if (
                product.type === "fractional" &&
                product.fractions &&
                product.fractions.length > 0
              ) {
                const tempVariation = product.variations[0];
                product.variations = [];
                if (
                  product.typeFractional === "proportional" &&
                  product.quantityFractional
                ) {
                  let priceVariation = 0;
                  product.fractions
                    .sort((a, b) =>
                      a.variations[0].price > b.variations[0].price
                        ? 1
                        : b.variations[0].price > a.variations[0].price
                        ? -1
                        : 0
                    )
                    .forEach((fractional, index) => {
                      if (
                        product.quantityFractional &&
                        index < product.quantityFractional
                      ) {
                        priceVariation += fractional.variations[0].price;
                      }
                    });
                  product.variations[0] = {
                    price: priceVariation / product.quantityFractional,
                    context: tempVariation.context,
                    id: tempVariation.id,
                    img: tempVariation.img,
                    inventory: tempVariation.inventory,
                    locationId: tempVariation.locationId,
                    minimumStock: tempVariation.minimumStock,
                    promotionalPrice: tempVariation.promotionalPrice,
                    referenceCode: tempVariation.referenceCode,
                    sku: tempVariation.sku,
                    status: tempVariation.status,
                  };
                } else if (product.typeFractional === "biggest") {
                  let priceVariation = 0;
                  product.fractions
                    .sort((a, b) =>
                      a.variations[0].price > b.variations[0].price
                        ? 1
                        : b.variations[0].price > a.variations[0].price
                        ? -1
                        : 0
                    )
                    .forEach((fractional) => {
                      if (fractional.variations[0].price > priceVariation)
                        priceVariation = fractional.variations[0].price;
                    });
                  product.variations[0] = {
                    price: priceVariation,
                    context: tempVariation.context,
                    id: tempVariation.id,
                    img: tempVariation.img,
                    inventory: tempVariation.inventory,
                    locationId: tempVariation.locationId,
                    minimumStock: tempVariation.minimumStock,
                    promotionalPrice: tempVariation.promotionalPrice,
                    referenceCode: tempVariation.referenceCode,
                    sku: tempVariation.sku,
                    status: tempVariation.status,
                  };
                }
              }
            });
            response.data.data.forEach((product: ProductType) => {
              if (
                product.variations[0] &&
                product.variations[0].img &&
                !product.variations[0].img.includes("http")
              ) {
                product.variations[0].img = `https://s3.amazonaws.com/img.ninegrid.com/${product.variations[0].img}`;
              }
              product.images = product.images.map((image) => {
                let imgProduct =
                  image.path.split("://")[1] ===
                  "s3.amazonaws.com/img.ninegrid.com"
                    ? image.path + "/" + image.imgsuffix
                    : image.path;

                image.imgsuffix = imgProduct;
                return image;
              });
              if (product.type !== "optional") newProducts.push(product);
            });
            const productsAdd = newProducts
              .filter(
                (product) =>
                  product.variations.length > 0 &&
                  parseInt(product.classificationId) > 0 &&
                  product.type !== "optional" &&
                  !product.sellAsFractional
              )
              .sort((a, b) =>
                a.order > b.order ? 1 : b.order > a.order ? -1 : 0
              );
            setProductsSaved([
              {
                products: productsAdd,
                page,
                qtdProductsPagination,
                hasNextPage,
                configProductSearch: {
                  isPriceGreaterThan: objSearch.isPriceGreaterThan,
                  isInventoryGreaterThan: objSearch.isInventoryGreaterThan,
                  name: objSearch.name,
                  status: objSearch.status,
                  variationActive: objSearch.variationActive,
                  validClassification: objSearch.validClassification,
                  classificationIds: objSearch.classificationIds
                    ? objSearch.classificationIds
                    : null,
                },
              },
              ...productsSaved,
            ]);
            return {
              products: productsAdd,
              hasNextPage,
              meta: response.data.meta,
            };
          })
          .catch((err) => {
            console.log(err);
            return {
              products: [],
              hasNextPage: false,
              meta: null,
            };
          });
      } else {
        let orderByField = "id";
        let orderByAscDesc = "ASC";

        switch (orderBy) {
          case "name-asc":
            orderByField = "name";
            orderByAscDesc = "ASC";
            break;
          case "name-desc":
            orderByField = "name";
            orderByAscDesc = "DESC";
            break;
          case "price-asc":
            orderByField = "price";
            orderByAscDesc = "ASC";
            break;
          case "price-desc":
            orderByField = "price";
            orderByAscDesc = "DESC";
            break;
          case "created-asc":
            orderByField = "created";
            orderByAscDesc = "ASC";
            break;
          case "created-desc":
            orderByField = "created";
            orderByAscDesc = "DESC";
            break;
        }

        let url = `api/product/newFilter_v2?page=${page}&take=${qtdProductsPagination}&field=${orderByField}&order=${orderByAscDesc}`;

        return await api
          .post(url, objSearch, {
            headers: {
              Authorization: `Bearer ${await getToken()}`,
            },
          })
          .then(async (response) => {
            const hasNextPage = response.data.meta.hasNextPage;
            response.data.data.map((product: ProductType) => {
              if (
                product.type === "fractional" &&
                product.fractions &&
                product.fractions.length > 0
              ) {
                const tempVariation = product.variations[0];
                product.variations = [];
                if (
                  product.typeFractional === "proportional" &&
                  product.quantityFractional
                ) {
                  let priceVariation = 0;
                  product.fractions
                    .sort((a, b) =>
                      a.variations[0].price > b.variations[0].price
                        ? 1
                        : b.variations[0].price > a.variations[0].price
                        ? -1
                        : 0
                    )
                    .forEach((fractional, index) => {
                      if (
                        product.quantityFractional &&
                        index < product.quantityFractional
                      ) {
                        priceVariation += fractional.variations[0].price;
                      }
                    });
                  product.variations[0] = {
                    price: priceVariation / product.quantityFractional,
                    context: tempVariation.context,
                    id: tempVariation.id,
                    img: tempVariation.img,
                    inventory: tempVariation.inventory,
                    locationId: tempVariation.locationId,
                    minimumStock: tempVariation.minimumStock,
                    promotionalPrice: tempVariation.promotionalPrice,
                    referenceCode: tempVariation.referenceCode,
                    sku: tempVariation.sku,
                    status: tempVariation.status,
                  };
                } else if (product.typeFractional === "biggest") {
                  let priceVariation = 0;
                  product.fractions
                    .sort((a, b) =>
                      a.variations[0].price > b.variations[0].price
                        ? 1
                        : b.variations[0].price > a.variations[0].price
                        ? -1
                        : 0
                    )
                    .forEach((fractional) => {
                      if (fractional.variations[0].price > priceVariation)
                        priceVariation = fractional.variations[0].price;
                    });
                  product.variations[0] = {
                    price: priceVariation,
                    context: tempVariation.context,
                    id: tempVariation.id,
                    img: tempVariation.img,
                    inventory: tempVariation.inventory,
                    locationId: tempVariation.locationId,
                    minimumStock: tempVariation.minimumStock,
                    promotionalPrice: tempVariation.promotionalPrice,
                    referenceCode: tempVariation.referenceCode,
                    sku: tempVariation.sku,
                    status: tempVariation.status,
                  };
                }
              }
            });
            response.data.data.forEach((product: ProductType) => {
              if (
                product.variations[0] &&
                product.variations[0].img &&
                !product.variations[0].img.includes("http")
              ) {
                product.variations[0].img = `https://s3.amazonaws.com/img.ninegrid.com/${product.variations[0].img}`;
              }
              product.images = product.images.map((image) => {
                let imgProduct =
                  image.path.split("://")[1] ===
                  "s3.amazonaws.com/img.ninegrid.com"
                    ? image.path + "/" + image.imgsuffix
                    : image.path;

                image.imgsuffix = imgProduct;
                return image;
              });
              if (product.type !== "optional") newProducts.push(product);
            });
            const productsAdd = newProducts.filter(
              (product) =>
                product.variations.length > 0 &&
                parseInt(product.classificationId) > 0 &&
                product.type !== "optional" &&
                !product.sellAsFractional
            );
            setProductsSaved([
              {
                products: productsAdd,
                page,
                qtdProductsPagination,
                hasNextPage,
                configProductSearch: {
                  isPriceGreaterThan: objSearch.isPriceGreaterThan,
                  isInventoryGreaterThan: objSearch.isInventoryGreaterThan,
                  name: objSearch.name,
                  status: objSearch.status,
                  variationActive: objSearch.variationActive,
                  validClassification: objSearch.validClassification,
                  classificationIds: objSearch.classificationIds
                    ? objSearch.classificationIds
                    : null,
                },
              },
              ...productsSaved,
            ]);
            return {
              products: productsAdd,
              hasNextPage,
              meta: response.data.meta,
            };
          })
          .catch((err) => {
            console.log(err);
            return {
              products: [],
              hasNextPage: false,
              meta: null,
            };
          });
      }
    }
  }

  function selectProduct(id: string) {
    let pdtSelected: ProductType | null | undefined = null;
    productsSaved.forEach((productConfig) => {
      if (!pdtSelected) {
        pdtSelected = productConfig.products.find(
          (product) => product.id === id
        );
      }
    });
    setProductSelected(pdtSelected);
    return pdtSelected;
  }

  function selectProductFood(id: string) {
    if (!productsFoodSaved) return null;

    let pdtSelected = productsFoodSaved.find(
      (product) => product.id.toString() === id
    );
    setProductFoodSelected(pdtSelected ? pdtSelected : null);
    return pdtSelected ? pdtSelected : null;
  }

  function organizeFractions(fractions: ItemFractionalType[]) {
    let newFractions: ItemFractionalType[] = [];
    fractions.forEach((fraction) => {
      const existedFractionIndex = newFractions.findIndex(
        (fractionAdded) => fractionAdded.id === fraction.id
      );
      if (existedFractionIndex === -1) {
        newFractions.push({ ...fraction });
      } else {
        newFractions[existedFractionIndex].quantity++;
      }
    });
    return newFractions;
  }

  function saveFilterProductOptions(
    filterProductOptions: FilterProductOptions
  ) {
    const encodedFilters = btoa(JSON.stringify(filterProductOptions));
    localStorage.setItem("filterProductOptions", encodedFilters);
    setFilterProductOptions(filterProductOptions);
  }

  function getFilterProductOptions(): FilterProductOptions {
    const encodedFilters = localStorage.getItem("filterProductOptions");
    if (encodedFilters) {
      const decodedFilters = atob(encodedFilters);
      const parsedFilters = JSON.parse(decodedFilters);

      setFilterProductOptions(parsedFilters);
      return parsedFilters;
    }

    if (filterProductOptions) {
      return filterProductOptions;
    }

    return {
      name: "",
      categories: [],
      prices: {
        min: 0,
        max: 0,
      },
    };
  }

  async function getGrades(
    categoriasId: string[],
    noStock: boolean,
    name?: string
  ) {
    try {
      const bodyFilter: any = {
        categorias: categoriasId,
        isPriceGreaterThan: 0,
        isInventoryGreaterThan: noStock ? 0 : 1,
        status: true,
        variationActive: true,
        validClassification: true,
      };

      if (name) {
        bodyFilter["name"] = name;
      }

      const response = await api
        .post("api/product/grade_filter_products", bodyFilter, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((response) => response.data.data)
        .catch((err) => {
          console.log(err);
          return [];
        });

      return response;
    } catch (error) {
      Swal.fire({
        icon: "warning",
        title: "Atenção",
        text: "Não foi possível carregar as opções de filtro do produto.",
      });
      return [];
    }
  }

  async function getGradesByCatalog(catalog_id: string, noStock: boolean) {
    try {
      const translateCatalogId = atob(catalog_id);

      const bodyFilter = {
        catalog_id: translateCatalogId,
        isPriceGreaterThan: 0,
        isInventoryGreaterThan: noStock ? 0 : 1,
        status: true,
        variationActive: true,
        validClassification: true,
      };

      const response = await api
        .post("api/product/grade_filter_products", bodyFilter, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((response) => response.data.data)
        .catch((err) => {
          console.log(err);
          return [];
        });

      return response;
    } catch (error) {
      Swal.fire({
        icon: "warning",
        title: "Atenção",
        text: "Não foi possível carregar as opções de filtro do produto.",
      });
      return [];
    }
  }

  async function getProdutosFiltered(
    page: number,
    pageSize: number,
    showOutStock: boolean,
    orderBy?: string
  ) {
    try {
      const filtro = getFilterProductOptions();
      const newFilter: any = {
        isPriceGreaterThan: 0,
        isInventoryGreaterThan: showOutStock ? 0 : 1,
        status: true,
        variationActive: true,
        validClassification: true,
        ...filtro,
      };

      let orderByField = "id";
      let orderByAscDesc = "ASC";

      switch (orderBy) {
        case "name-asc":
          orderByField = "name";
          orderByAscDesc = "ASC";
          break;
        case "name-desc":
          orderByField = "name";
          orderByAscDesc = "DESC";
          break;
        case "price-asc":
          orderByField = "price";
          orderByAscDesc = "ASC";
          break;
        case "price-desc":
          orderByField = "price";
          orderByAscDesc = "DESC";
          break;
        case "created-asc":
          orderByField = "created";
          orderByAscDesc = "ASC";
          break;
        case "created-desc":
          orderByField = "created";
          orderByAscDesc = "DESC";
          break;
      }

      delete newFilter.name;
      const searchTerm = filtro.name;

      const url = `api/product/filter_products_varejo?page=${page}&take=${pageSize}&search=${searchTerm}&field=${orderByField}&order=${orderByAscDesc}`;
      const response = await api
        .post(url, newFilter, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((response) => {
          const retorno = response.data;
          let newProducts: ProductType[] = [];

          if (retorno.data && retorno.data.length > 0) {
            response.data.data.map((product: ProductType) => {
              if (
                product.type === "fractional" &&
                product.fractions &&
                product.fractions.length > 0
              ) {
                const tempVariation = product.variations[0];
                product.variations = [];
                if (
                  product.typeFractional === "proportional" &&
                  product.quantityFractional
                ) {
                  let priceVariation = 0;
                  product.fractions
                    .sort((a, b) =>
                      a.variations[0].price > b.variations[0].price
                        ? 1
                        : b.variations[0].price > a.variations[0].price
                        ? -1
                        : 0
                    )
                    .forEach((fractional, index) => {
                      if (
                        product.quantityFractional &&
                        index < product.quantityFractional
                      ) {
                        priceVariation += fractional.variations[0].price;
                      }
                    });
                  product.variations[0] = {
                    price: priceVariation / product.quantityFractional,
                    context: tempVariation.context,
                    id: tempVariation.id,
                    img: tempVariation.img,
                    inventory: tempVariation.inventory,
                    locationId: tempVariation.locationId,
                    minimumStock: tempVariation.minimumStock,
                    promotionalPrice: tempVariation.promotionalPrice,
                    referenceCode: tempVariation.referenceCode,
                    sku: tempVariation.sku,
                    status: tempVariation.status,
                  };
                } else if (product.typeFractional === "biggest") {
                  let priceVariation = 0;
                  product.fractions
                    .sort((a, b) =>
                      a.variations[0].price > b.variations[0].price
                        ? 1
                        : b.variations[0].price > a.variations[0].price
                        ? -1
                        : 0
                    )
                    .forEach((fractional) => {
                      if (fractional.variations[0].price > priceVariation)
                        priceVariation = fractional.variations[0].price;
                    });
                  product.variations[0] = {
                    price: priceVariation,
                    context: tempVariation.context,
                    id: tempVariation.id,
                    img: tempVariation.img,
                    inventory: tempVariation.inventory,
                    locationId: tempVariation.locationId,
                    minimumStock: tempVariation.minimumStock,
                    promotionalPrice: tempVariation.promotionalPrice,
                    referenceCode: tempVariation.referenceCode,
                    sku: tempVariation.sku,
                    status: tempVariation.status,
                  };
                }
              }
            });
            response.data.data.forEach((product: ProductType) => {
              if (
                product.variations[0] &&
                product.variations[0].img &&
                !product.variations[0].img.includes("http")
              ) {
                product.variations[0].img = `https://s3.amazonaws.com/img.ninegrid.com/${product.variations[0].img}`;
              }
              product.images = product.images.map((image) => {
                let imgProduct =
                  image.path.split("://")[1] ===
                  "s3.amazonaws.com/img.ninegrid.com"
                    ? image.path + "/" + image.imgsuffix
                    : image.path;

                image.imgsuffix = imgProduct;
                return image;
              });
              if (product.type !== "optional") newProducts.push(product);
            });
            const productsAdd = newProducts.filter(
              (product) =>
                product.variations.length > 0 &&
                parseInt(product.classificationId) > 0 &&
                product.type !== "optional" &&
                !product.sellAsFractional
            );
          }

          return {
            products: newProducts,
            ...response.data,
          };
        })
        .catch((err) => {
          console.log(err);
          return {
            products: [],
            hasNextPage: false,
            meta: null,
          };
        });

      return response;
    } catch (error) {
      console.log(error);
      return {
        products: [],
        hasNextPage: false,
        meta: null,
      };
    }
  }

  async function getProdutosFilteredByCatalog(
    catalog_id: string,
    page: number,
    pageSize: number,
    showOutStock: boolean,
    orderBy?: string,
    firstTime?: boolean
  ) {
    try {
      const filtro = getFilterProductOptions();
      let newFilter: any = {
        isPriceGreaterThan: 0,
        isInventoryGreaterThan: showOutStock ? 0 : 1,
        status: true,
        variationActive: true,
        validClassification: true,
        catalog_id: catalog_id,
      };

      if (!firstTime) {
        newFilter = { ...filtro, ...newFilter };
      }

      let orderByField = "id";
      let orderByAscDesc = "ASC";

      switch (orderBy) {
        case "name-asc":
          orderByField = "name";
          orderByAscDesc = "ASC";
          break;
        case "name-desc":
          orderByField = "name";
          orderByAscDesc = "DESC";
          break;
        case "price-asc":
          orderByField = "price";
          orderByAscDesc = "ASC";
          break;
        case "price-desc":
          orderByField = "price";
          orderByAscDesc = "DESC";
          break;
        case "created-asc":
          orderByField = "created";
          orderByAscDesc = "ASC";
          break;
        case "created-desc":
          orderByField = "created";
          orderByAscDesc = "DESC";
          break;
      }

      delete newFilter.name;
      const searchTerm = filtro.name;

      const url = `api/product/filter_products_varejo?page=${page}&take=${pageSize}&search=${searchTerm}&field=${orderByField}&order=${orderByAscDesc}`;
      const response = await api
        .post(url, newFilter, {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        })
        .then((response) => {
          const retorno = response.data;
          let newProducts: ProductType[] = [];

          if (retorno.data && retorno.data.length > 0) {
            response.data.data.forEach((product: ProductType) => {
              if (
                product.variations[0] &&
                product.variations[0].img &&
                !product.variations[0].img.includes("http")
              ) {
                product.variations[0].img = `https://s3.amazonaws.com/img.ninegrid.com/${product.variations[0].img}`;
              }
              product.images = product.images.map((image) => {
                let imgProduct =
                  image.path.split("://")[1] ===
                  "s3.amazonaws.com/img.ninegrid.com"
                    ? image.path + "/" + image.imgsuffix
                    : image.path;

                image.imgsuffix = imgProduct;
                return image;
              });
              if (product.type !== "optional") newProducts.push(product);
            });
            const productsAdd = newProducts.filter(
              (product) =>
                product.variations.length > 0 &&
                parseInt(product.classificationId) > 0 &&
                product.type !== "optional" &&
                !product.sellAsFractional
            );
          }

          return {
            products: newProducts,
            ...response.data,
          };
        })
        .catch((err) => {
          console.log(err);
          return {
            products: [],
            hasNextPage: false,
            meta: null,
          };
        });

      return response;
    } catch (error) {
      console.log(error);
      return {
        products: [],
        hasNextPage: false,
        meta: null,
      };
    }
  }

  function saveOrderBy(orderBy: string) {
    localStorage.setItem("orderByProducts", orderBy);
    setOrderBy(orderBy);
  }

  function getOrderBy(): string {
    const orderBy = localStorage.getItem("orderByProducts");
    if (orderBy) {
      setOrderBy(orderBy);
      return orderBy;
    }
    return "name-asc";
  }
  // Funções para controle de orderBy por página
  function saveOrderByForPage(
    page: "pdv" | "pdvFiltered" | "pdvFilteredCategory",
    orderBy: string,
    category?: string
  ) {
    const currentControl = getOrderByControlFromStorage();

    if (page === "pdvFilteredCategory") {
      // Para página pdvFilteredCategory (filtro por categoria), salva o orderBy apenas se for da mesma categoria
      if (category && currentControl.currentCategory === category) {
        currentControl.pdvFilteredCategory = orderBy;
      } else if (category) {
        // Se mudou de categoria, usa o orderBy da página pdv
        currentControl.pdvFilteredCategory = currentControl.pdv;
        currentControl.currentCategory = category;
      }
    } else if (page === "pdv") {
      // Para página pdv (também controlada por categoria), salva o orderBy apenas se for da mesma categoria
      if (category && currentControl.currentCategory === category) {
        currentControl.pdv = orderBy;
      } else if (category) {
        // Se mudou de categoria, reseta para default
        currentControl.pdv = orderBy;
        currentControl.currentCategory = category;
      } else {
        // Se não tem categoria, salva normalmente
        currentControl.pdv = orderBy;
      }
    } else {
      // Para página pdvFiltered, salva normalmente
      currentControl[page] = orderBy;
    }

    currentControl.currentPage = page;
    saveOrderByControlToStorage(currentControl);
    setOrderByPageControl(currentControl);
  }
  function getOrderByForPage(
    page: "pdv" | "pdvFiltered" | "pdvFilteredCategory",
    category?: string
  ): string {
    const currentControl = getOrderByControlFromStorage();

    if (page === "pdvFilteredCategory" && category) {
      // Se estiver indo para página pdvFilteredCategory com categoria diferente, usa orderBy da página pdv
      if (currentControl.currentCategory !== category) {
        return currentControl.pdv;
      }
    }

    if (page === "pdv" && category) {
      // Se estiver indo para página pdv com categoria diferente, reseta para default
      if (currentControl.currentCategory !== category) {
        return "name-asc";
      }
    }

    return currentControl[page] || "name-asc";
  }
  function resetOrderByForPage(
    page: "pdv" | "pdvFiltered" | "pdvFilteredCategory"
  ) {
    const currentControl = getOrderByControlFromStorage();
    currentControl[page] = "name-asc";

    if (page === "pdvFilteredCategory" || page === "pdv") {
      currentControl.currentCategory = undefined;
    }

    saveOrderByControlToStorage(currentControl);
    setOrderByPageControl(currentControl);
  }
  function setCurrentPage(
    page: "pdv" | "pdvFiltered" | "pdvFilteredCategory",
    category?: string
  ) {
    const currentControl = getOrderByControlFromStorage();

    // Se mudando para página pdvFiltered, reset do orderBy das páginas que dependem de categoria
    if (
      page === "pdvFiltered" &&
      (currentControl.currentPage === "pdv" ||
        currentControl.currentPage === "pdvFilteredCategory")
    ) {
      currentControl.pdv = "name-asc";
      currentControl.pdvFilteredCategory = "name-asc";
      currentControl.currentCategory = undefined;
    }

    // Se mudando para página pdv com categoria diferente, reseta o orderBy
    if (
      page === "pdv" &&
      category &&
      currentControl.currentCategory !== category
    ) {
      currentControl.pdv = "name-asc";
      currentControl.pdvFilteredCategory = "name-asc";
      currentControl.currentCategory = category;
    }

    // Se mudando para página pdvFilteredCategory com categoria diferente, usa orderBy da página pdv
    if (
      page === "pdvFilteredCategory" &&
      category &&
      currentControl.currentCategory !== category
    ) {
      currentControl.pdvFilteredCategory = currentControl.pdv;
      currentControl.currentCategory = category;
    }

    // Se mudando de pdvFiltered para pdv/pdvFilteredCategory, reset do pdvFiltered
    if (
      (page === "pdv" || page === "pdvFilteredCategory") &&
      currentControl.currentPage === "pdvFiltered"
    ) {
      currentControl.pdvFiltered = "name-asc";
    }

    currentControl.currentPage = page;
    saveOrderByControlToStorage(currentControl);
    setOrderByPageControl(currentControl);
  }

  // Funções auxiliares para localStorage
  function getOrderByControlFromStorage(): OrderByPageControl {
    const stored = localStorage.getItem("orderByPageControl");
    if (stored) {
      try {
        return JSON.parse(stored);
      } catch (error) {
        console.error(
          "Erro ao parsear orderByPageControl do localStorage:",
          error
        );
      }
    }
    return {
      pdv: "name-asc",
      pdvFiltered: "name-asc",
      pdvFilteredCategory: "name-asc",
      currentPage: "pdv",
      currentCategory: undefined,
    };
  }

  function saveOrderByControlToStorage(control: OrderByPageControl) {
    localStorage.setItem("orderByPageControl", JSON.stringify(control));
  }

  function saveCurrentCategoryFilter(category: string) {
    localStorage.setItem("currentCategoryFilter", category);
  }

  function getCurrentCategoryFilter(): string {
    return localStorage.getItem("currentCategoryFilter") || "";
  }

  return (
    <ProductsContext.Provider
      value={{
        getProducts,
        getProdutosFiltered,
        getProductsFood,
        selectProduct,
        productSelected,
        selectProductFood,
        productFoodSelected,
        organizeFractions,

        getFilterProductOptions,
        saveFilterProductOptions,
        getGrades,
        setOpenModalFiltroPDV,
        openModalFiltroPDV,
        getGradesByCatalog,
        getProdutosFilteredByCatalog,

        // Novas funções para controle de orderBy por página
        saveOrderByForPage,
        getOrderByForPage,
        resetOrderByForPage,
        setCurrentPage,

        saveCurrentCategoryFilter,
        getCurrentCategoryFilter,
      }}
    >
      {props.children}
    </ProductsContext.Provider>
  );
}
